import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/components/common/text-input'
import { Dropdown } from '@/components/ui/modal';
import CheckboxGroup from '@/components/common/check-box-group';
import {
  agentPermissionHooks,
  CREATE_FORM_PERMISSION_KEYS,
  CreateAdminFormData,
  isOpsFeatureEnabled,
  PERMISSION_MAP,
  ROLE_TYPE_MAP,
} from '@/manager/manager/agent-dashboard-permission-manager';
import { cn } from '@/lib/utils';
import { AgentOpsFeatureConfigData } from '@/services/agent-ops-feature-config';

const CREATE_PERMISSION_LABEL_KEYS: Record<
  (typeof CREATE_FORM_PERMISSION_KEYS)[number],
  string
> = {
  user: 'agent_dashboard.permission.create_modal.user_management',
  permission: 'agent_dashboard.permission.create_modal.permission_management',
  data: 'agent_dashboard.permission.create_modal.data_overview',
  content: 'agent_dashboard.permission.create_modal.content_management',
  order: 'agent_dashboard.permission.create_modal.order_management',
  company: 'agent_dashboard.permission.create_modal.company_management',
};

type CreateFormData = Omit<CreateAdminFormData, 'agentId'>
export interface CreateFormProps extends CreateFormData {
  isEdit?: boolean;
  loading?: boolean;
  userNameError?: string;
  emailError?: string;
  roleTypeError?: string;
  customPermission: string;
  opsFeatureConfigData: AgentOpsFeatureConfigData;
  onCustomPermissionChange: (value: string) => void;
  onValueChange?: (data: { name: keyof CreateFormData; value: string | number | string[] }) => void;
}

const Label = ({ label, icon }: { label: string; icon?: React.ReactNode }) => {
  return (
    <label className='flex w-48 mb-2'>
      {icon}
      <span className='text-sm font-medium text-[rgba(52,64,84,1)] mr-0.5'>{label}</span>
      <span className='text-[rgba(117,66,247,1)]'>*</span>
    </label>
  )
}

/** 创建管理员表单 */
export const CreateForm = ({
  isEdit,
  loading,
  userName,
  email,
  roleType,
  permissions,
  customPermission,
  opsFeatureConfigData,
  userNameError,
  emailError,
  roleTypeError,
  onValueChange,
  onCustomPermissionChange,
}: CreateFormProps) => {
  const { t } = useTranslation();

  const handleChange = (name: keyof CreateFormData, value: string | string[]) => {
    onValueChange && onValueChange({ name, value });
  };

  const visiblePermissionOptions = useMemo(
    () =>
      CREATE_FORM_PERMISSION_KEYS.filter((key) =>
        isOpsFeatureEnabled(key, opsFeatureConfigData)
      ).map((key) => ({
        value: PERMISSION_MAP[key],
        label: t(CREATE_PERMISSION_LABEL_KEYS[key]),
      })),
    [opsFeatureConfigData, t]
  );

  return (
    <div className='w-full h-full space-y-5 flex-shrink-0 select-none'>
      <div>
        <div className='mb-3 text-base font-medium text-[rgba(24,29,39,1)]'>
          {t('agent_dashboard.permission.create_modal.basic_info')}
        </div>
        <div className='space-y-5'>
          <div>
            <Label label={t('agent_dashboard.permission.create_modal.user_email')} />
            <div>
              <TextInput
                type="text"
                value={email}
                placeholder={t('agent_dashboard.permission.create_modal.user_email_placeholder')}
                className=' h-11 !text-sm !placeholder:text-sm'
                onChange={(e) => handleChange('email', e.target.value)}
                error={!!emailError}
                helperText={emailError}
                disabled={loading || isEdit}
              />
            </div>
          </div>
          <div>
            <Label label={t('agent_dashboard.permission.create_modal.name')} />
            <div>
              <TextInput
                type="text"
                value={userName}
                placeholder={t('agent_dashboard.permission.create_modal.name_placeholder')}
                className='h-11 !font-normal'
                onChange={(e) => handleChange('userName', e.target.value)}
                error={!!userNameError}
                helperText={userNameError}
                disabled={loading}
              />
            </div>
          </div>
          <div>
            <Label label={t('agent_dashboard.permission.create_modal.role')} />
            <div>
              <Dropdown
                value={roleType?.toString()}
                placeholder={t('agent_dashboard.permission.create_modal.role_placeholder')}
                className={cn(
                  'h-11 !text-base !font-normal',
                  roleTypeError && "border-[rgba(217,45,32,1)] hover:border-[rgba(217,45,32,1)] focus:border-[rgba(217,45,32,1)]"
                )}
                disabled={loading}
                width='40rem'
                onValueChange={(value) => handleChange('roleType', value)}
                options={[
                  { value: ROLE_TYPE_MAP.admin.toString(), label: t('agent_dashboard.permission.create_modal.admin') },
                  { value: ROLE_TYPE_MAP.operator.toString(), label: t('agent_dashboard.permission.create_modal.operator') },
                ]}
              />
              {roleTypeError && <div className='text-[rgba(217,45,32,1)] text-xs pl-1 pt-0.5'>{roleTypeError}</div>}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className='flex items-center justify-between mb-3'>
          <span className='text-base font-medium text-[rgba(24,29,39,1)]'>
            {t('agent_dashboard.permission.create_modal.permission_setting')}
          </span>
          <CheckboxGroup.Root
            name="example"
            value={[customPermission]}
            className='text-sm'
            onChange={(values) => {
              onCustomPermissionChange(values.includes('1') ? '1' : '0');
            }}
          >
            <CheckboxGroup.Item value={'1'}>{t('agent_dashboard.permission.create_modal.custom_permission')}</CheckboxGroup.Item>
          </CheckboxGroup.Root>
        </div>
        <CheckboxGroup.Root
          name="example"
          defaultValue={permissions}
          disabled={!Number(customPermission)}
          onChange={(values) => handleChange('permissions', values)}
          className='space-y-3 w-60 text-sm'
        >
          {visiblePermissionOptions.map((option) => (
            <CheckboxGroup.Item key={option.value} value={option.value}>
              {option.label}
            </CheckboxGroup.Item>
          ))}
        </CheckboxGroup.Root>
        {!Number(customPermission) && (
          <div className='mt-3 flex items-center'>
            <span className='text-gray-500 text-sm'>{t('agent_dashboard.permission.create_modal.permission_tips')}</span>
          </div>
        )}
      </div>
    </div>
  )
}
